/**
 * Download the current list as CSV.
 *
 * Takes the same filters the page is showing, so the file always matches what
 * is on screen. An oversized export comes back as a 413 with a message telling
 * the user to narrow the range — surfaced as-is rather than handing over a
 * quietly truncated file.
 */
import { useState } from "react";
import { Download, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { staffApi } from "@/platform/staffApi";

export function ExportButton({
  dataset,
  params = "",
  label = "Export CSV",
}: {
  dataset: string;
  params?: string;
  label?: string;
}) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      await staffApi.downloadCsv(dataset, params, `${dataset}.csv`);
    } catch (error) {
      toast({
        title: "Export failed",
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button variant="outline" size="sm" onClick={run} disabled={busy}>
      {busy ? (
        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
      ) : (
        <Download className="mr-1 h-4 w-4" />
      )}
      {label}
    </Button>
  );
}
